import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// 1. Ant Design
import { CloseOutlined, LogoutOutlined, MenuFoldOutlined, MenuOutlined, MenuUnfoldOutlined, MessageOutlined, UserOutlined, } from "@ant-design/icons";
import { Badge, Button, Layout, Menu, Space, Tag, Tooltip, Typography } from "antd";
// 2. External libraries
import { observer } from "mobx-react-lite";
import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useLocation, useNavigate } from "react-router";
// 3. Internal components
import ChatPanel from "@/components/Chat/ChatPanel";
import { filterStaffMenu, STAFF_MENU, STAFF_MENU_PARENT_BY_PATH } from "@/components/navigation/staffMenu";
// 4. Stores
import { getRootStore } from "@/stores/RootStore";
// 5. Types, utils, styles
import { canAccessChat } from "@/utils/permissions";
import styles from "@/styles/AppLayout.module.less";
const { Header, Sider, Content } = Layout;
const { Title, Text } = Typography;
// Информация о версии приложения
const appVersion = process.env.REACT_APP_VERSION || "0.0.0";
const gitCommit = process.env.REACT_APP_GIT_COMMIT || "dev";
const buildTime = process.env.REACT_APP_BUILD_TIME;
const environment = process.env.REACT_APP_ENVIRONMENT || "Dev";
const fullVersion = `${appVersion}-${gitCommit}`;
const formattedBuildTime = buildTime
    ? new Date(buildTime).toLocaleString("ru-RU")
    : "";
// Брейкпоинт для мобильных устройств (соответствует lg в Ant Design)
const MOBILE_BREAKPOINT = 992;
// Ширина свёрнутого меню на десктопе (только иконки)
const COLLAPSED_WIDTH = 64;
// Длительность подсветки кнопки «Чат» на новое сообщение (две пульсации по 1,2 с)
const CHAT_ATTENTION_MS = 2400;
const AppLayout = observer(({ children }) => {
    const { authStore, chatStore } = getRootStore();
    const navigate = useNavigate();
    const location = useLocation();
    // WP-DSP-FIX-01 (UC1001 BR-2, UC1005 BR-1): чат подключается сразу после входа персонала — панель закрыта,
    // счётчик непрочитанного и звук работают без неё. Оболочка живёт ровно сессию персонала, поэтому её
    // размонтирование (выход, сессия другого пользователя) закрывает соединение.
    const canChat = canAccessChat(authStore.userRole);
    const sessionUserId = authStore.user?.id ?? null;
    useEffect(() => {
        if (!canChat || !sessionUserId)
            return;
        chatStore.initChat({ silent: true });
        return () => chatStore.disconnect();
    }, [canChat, sessionUserId]);
    const unreadCount = chatStore.totalUnreadCount;
    const chatAttentionAt = chatStore.attentionAt;
    const chatError = chatStore.error;
    // Подсветка кнопки на новое сообщение — классом на время анимации, без пересоздания кнопки
    // (иначе теряется фокус и может пропасть клик)
    const [chatHighlighted, setChatHighlighted] = useState(false);
    useEffect(() => {
        if (chatAttentionAt === 0)
            return;
        setChatHighlighted(true);
        const timer = setTimeout(() => setChatHighlighted(false), CHAT_ATTENTION_MS);
        return () => clearTimeout(timer);
    }, [chatAttentionAt]);
    // Состояние для десктопного сворачивания (mini sidebar)
    const [desktopCollapsed, setDesktopCollapsed] = useState(false);
    // Состояние для мобильного скрытия (полностью скрыто)
    const [mobileHidden, setMobileHidden] = useState(true);
    // Отслеживание мобильного режима
    const [isMobileView, setIsMobileView] = useState(false);
    // Проверка и обновление режима при изменении размера окна
    useEffect(() => {
        const checkMobile = () => {
            setIsMobileView(window.innerWidth <= MOBILE_BREAKPOINT);
        };
        checkMobile();
        window.addEventListener("resize", checkMobile);
        return () => window.removeEventListener("resize", checkMobile);
    }, []);
    // Навигация с автоматическим сворачиванием меню на мобильных
    const handleNavigate = useCallback((path) => {
        navigate(path);
        if (isMobileView) {
            setMobileHidden(true);
        }
    }, [navigate, isMobileView]);
    // Переключение меню на мобильных (показать/скрыть)
    const toggleMobileSider = useCallback(() => {
        setMobileHidden(!mobileHidden);
    }, [mobileHidden]);
    // Переключение меню на десктопе (свернуть/развернуть)
    const toggleDesktopSider = useCallback(() => {
        setDesktopCollapsed(!desktopCollapsed);
    }, [desktopCollapsed]);
    const handleLogout = async () => {
        await authStore.signOut();
    };
    const handleProfileClick = () => {
        navigate("/profile");
    };
    // Определяем открытые подменю на основе текущего пути
    const openKeys = useMemo(() => {
        const parentKey = STAFF_MENU_PARENT_BY_PATH[location.pathname];
        return parentKey ? [parentKey] : [];
    }, [location.pathname]);
    // Пункты меню персонала, отфильтрованные по Permission роли и флагу сборки B2B
    const menuItems = useMemo(() => {
        const buildItems = (staffItems) => staffItems.map((item) => {
            const icon = item.icon ? React.createElement(item.icon) : undefined;
            const label = item.testId ? (_jsx("span", { "data-testid": item.testId, children: item.label })) : (item.label);
            return item.children
                ? {
                    key: item.key,
                    icon,
                    label,
                    children: buildItems(item.children),
                }
                : {
                    key: item.key,
                    icon,
                    label,
                    onClick: () => handleNavigate(item.key),
                };
        });
        return buildItems(filterStaffMenu(STAFF_MENU, authStore.userRole));
    }, [authStore.userRole, handleNavigate]);
    const selectedKey = location.pathname;
    // Определяем состояние collapsed и ширину в зависимости от режима
    const isCollapsed = isMobileView ? mobileHidden : desktopCollapsed;
    const collapsedWidth = isMobileView ? 0 : COLLAPSED_WIDTH;
    return (_jsxs(Layout, { className: styles.layout, children: [_jsxs(Sider, { width: 250, className: `${styles.sider} ${desktopCollapsed ? styles.siderCollapsed : ""}`, collapsedWidth: collapsedWidth, collapsed: isCollapsed, trigger: null, children: [_jsxs("div", { className: styles.siderHeader, children: [_jsxs("div", { className: styles.logo, children: [!desktopCollapsed && (_jsxs("div", { className: styles.logoContainer, children: [_jsx(Title, { level: 5, className: styles.logoText, children: "\u0414\u0438\u0441\u043F\u0435\u0442\u0447\u0435\u0440\u0438\u0437\u0430\u0446\u0438\u044F" }), _jsx(Text, { className: styles.logoSubtext, children: "\u00AB\u0417\u0430\u0440\u044F\u0434\u0430 \u0441\u0432\u043E\u0431\u043E\u0434\u044B\u00BB" }), _jsx(Tag, { color: environment === "Prod" ? "green" : "orange", className: styles.envBadge, children: environment }), _jsx(Tooltip, { title: `Сборка: ${formattedBuildTime}`, children: _jsxs(Text, { className: styles.versionText, children: ["v", fullVersion] }) })] })), desktopCollapsed && !isMobileView && (_jsx(Tooltip, { title: `${environment} | v${fullVersion}`, children: _jsxs("div", { className: styles.logoContainer, children: [_jsx(Title, { level: 4, className: styles.logoText, children: "\u0417\u0421" }), _jsx(Text, { className: styles.envBadgeCollapsed, children: environment.charAt(0) })] }) }))] }), isMobileView && (_jsx(Button, { type: "text", icon: _jsx(CloseOutlined, {}), onClick: toggleMobileSider, className: styles.closeButton }))] }), _jsx(Menu, { mode: "inline", selectedKeys: [selectedKey], defaultOpenKeys: desktopCollapsed ? [] : openKeys, inlineCollapsed: desktopCollapsed && !isMobileView, items: menuItems, className: styles.menu }), !isMobileView && (_jsx("div", { className: styles.collapseToggle, children: _jsx(Button, { type: "text", icon: desktopCollapsed ? _jsx(MenuUnfoldOutlined, {}) : _jsx(MenuFoldOutlined, {}), onClick: toggleDesktopSider, className: styles.collapseButton }) }))] }), _jsxs(Layout, { children: [_jsx(Header, { className: styles.header, children: _jsxs("div", { className: styles.headerContent, children: [_jsx(Button, { type: "text", icon: _jsx(MenuOutlined, {}), onClick: toggleMobileSider, className: styles.menuButton }), _jsxs(Space, { children: [canChat && (_jsx(Tooltip, { title: chatError ? "Чат не подключён — откройте, чтобы повторить" : "Открыть чат", children: _jsx("span", { className: chatHighlighted ? styles.chatAttention : styles.chatWrap, children: _jsx(Badge, { count: unreadCount, overflowCount: 99, offset: [-6, 2], "data-testid": "chat-unread-badge", children: _jsx(Button, { type: "text", icon: _jsx(MessageOutlined, {}), onClick: () => chatStore.openPanel(), className: `${styles.headerButton} ${chatError ? styles.chatButtonError : styles.chatButton}`, "data-testid": "chat-header-button", children: "\u0427\u0430\u0442" }) }) }) })), _jsx(Button, { type: "text", icon: _jsx(UserOutlined, {}), onClick: handleProfileClick, className: styles.headerButton, children: "\u041F\u0440\u043E\u0444\u0438\u043B\u044C" }), _jsx(Button, { type: "text", icon: _jsx(LogoutOutlined, {}), onClick: handleLogout, className: styles.headerButton, children: "\u0412\u044B\u0445\u043E\u0434" })] })] }) }), _jsx(Content, { children: children })] }), canChat && _jsx(ChatPanel, {})] }));
});
export default AppLayout;
